import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import TimerWidget from '../../components/timer/TimerWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Clock, 
  DollarSign, 
  FolderKanban, 
  CheckCircle2,
  Play,
  ArrowUpRight,
  Calendar,
  Lock,
  Star,
  TrendingUp,
  Award,
  Mail,
  FileText
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Locked Account Overlay Component (outside main component to avoid re-creation)
const LockedOverlay = ({ creativeStatus }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
    <Card className="max-w-lg mx-4 shadow-2xl border-0">
      <CardContent className="p-8 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-slate-400" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-800 mb-3">
          Compte en attente de mission
        </h2>
        
        <p className="text-slate-600 mb-6 leading-relaxed">
          Vous devez recevoir une nouvelle mission pour accéder au back-office.
        </p>
        
        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-5 mb-6 text-left">
          <div className="flex items-start gap-3">
            <Award className="w-6 h-6 text-cyan-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-slate-800 mb-1">
                Devenir un créatif intégré
              </h3>
              <p className="text-sm text-slate-600">
                Vous pouvez devenir un créatif intégré avec un contrat sur des missions 
                récurrentes si l&apos;agence vous le propose.
              </p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-cyan-200/50">
            <p className="text-sm text-slate-600 mb-3">Conditions requises :</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                {(creativeStatus?.progression?.projects_delivered || 0) >= 5 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                )}
                <span className={cn(
                  (creativeStatus?.progression?.projects_delivered || 0) >= 5 
                    ? "text-emerald-700" 
                    : "text-slate-600"
                )}>
                  Minimum 5 projets livrés ({creativeStatus?.progression?.projects_delivered || 0}/5)
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {(creativeStatus?.progression?.average_rating || 0) >= 17 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                )}
                <span className={cn(
                  (creativeStatus?.progression?.average_rating || 0) >= 17 
                    ? "text-emerald-700" 
                    : "text-slate-600"
                )}>
                  Note satisfaction ≥ 17/20 ({creativeStatus?.progression?.average_rating || 0}/20)
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                <span className="text-slate-600">
                  Validation par l&apos;agence
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => window.location.href = 'mailto:contact@msc-agence.com'}
          >
            <Mail className="w-4 h-4 mr-2" />
            Contacter l&apos;agence
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
            onClick={() => window.location.href = '/profile'}
          >
            <FileText className="w-4 h-4 mr-2" />
            Voir mon profil
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const TeamDashboard = () => {
  const { user, token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Creative-specific state
  const [creativeStatus, setCreativeStatus] = useState(null);
  const [isCreative, setIsCreative] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dashboardRes, tasksRes] = await Promise.all([
        axios.get(`${API}/dashboard/team-member`, { headers }),
        axios.get(`${API}/tasks/my-tasks`, { headers })
      ]);
      setDashboard(dashboardRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkCreativeStatus = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/missions/creative/${user.id}/status`, { headers });
      setCreativeStatus(response.data);
      setIsCreative(true);
      setIsLocked(response.data.creative_status === 'locked');
    } catch (error) {
      // User is not a creative partner, that's OK
      setIsCreative(false);
      setIsLocked(false);
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchData();
    checkCreativeStatus();
  }, [fetchData, checkCreativeStatus]);

  const startTimer = async (taskId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/time/start`, { task_id: taskId }, { headers });
      toast.success('Timer démarré !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Generate weekly data from real time entries or show zeros
  const weeklyData = React.useMemo(() => {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    
    return days.map((day, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      // For now, show 0 - will be populated from real data when time tracking is used
      return {
        day,
        hours: 0, // Real data would come from time entries grouped by day
        date: date.toISOString().split('T')[0]
      };
    });
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminée',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.pending)}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <TimerWidget visible={!!dashboard?.current_timer} />
      
      {/* Locked Overlay for Creative Partners */}
      {isCreative && isLocked && <LockedOverlay creativeStatus={creativeStatus} />}
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64",
        isCreative && isLocked && "blur-md pointer-events-none select-none"
      )}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="dashboard-title">
              Mon espace de travail
            </h1>
            {isCreative && creativeStatus?.creative_type === 'integre' && (
              <Badge className="bg-emerald-500 text-white">
                <Award className="w-3 h-3 mr-1" />
                Créatif Intégré
              </Badge>
            )}
          </div>
          <p className="text-[#64748b] mt-1">
            Bienvenue, {user?.first_name} !
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Creative Progress Card (for creative partners) */}
            {isCreative && creativeStatus?.creative_type !== 'integre' && (
              <Card className="card mb-6 border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <TrendingUp className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">Progression vers Créatif Intégré</h3>
                        <p className="text-sm text-slate-600">
                          {creativeStatus?.progression?.missing_projects > 0 
                            ? `Encore ${creativeStatus.progression.missing_projects} projet(s) à livrer`
                            : creativeStatus?.progression?.missing_rating_points > 0
                              ? `Améliorer votre note de ${creativeStatus.progression.missing_rating_points} points`
                              : "En attente de validation par l'agence"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <FolderKanban className="w-4 h-4 text-slate-400" />
                          <span className="font-bold text-slate-800">
                            {creativeStatus?.progression?.projects_delivered || 0}/5
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">Projets</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Star className="w-4 h-4 text-amber-400" />
                          <span className="font-bold text-slate-800">
                            {creativeStatus?.progression?.average_rating || 0}/20
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">Note</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="card card-hover" data-testid="stat-projects">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Projets assignés</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.assigned_projects || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#6366f1]/10 rounded-xl flex items-center justify-center">
                      <FolderKanban className="w-6 h-6 text-[#6366f1]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-tasks">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Tâches actives</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.active_tasks || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#f59e0b]/10 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-[#f59e0b]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-hours">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Heures ce mois</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {(dashboard?.hours_this_month || 0).toFixed(1)}h
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#1a365d]/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#1a365d]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-earnings">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Gains ce mois</p>
                      <p className="font-mono text-2xl font-bold text-[#00d4aa]">
                        {(dashboard?.earnings_this_month || 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tasks List */}
              <Card className="card lg:col-span-2" data-testid="tasks-list">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Mes tâches
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-[#00d4aa]">
                    Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tasks.slice(0, 5).map((task) => (
                      <div 
                        key={task.id}
                        className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:border-[#00d4aa]/50 transition-all group"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="font-medium text-[#1a365d]">{task.name}</h4>
                            {getStatusBadge(task.status)}
                          </div>
                          <p className="text-sm text-[#64748b]">
                            {task.estimated_hours}h estimées • {task.actual_hours?.toFixed(1) || 0}h réalisées
                          </p>
                        </div>
                        
                        {task.status !== 'completed' && !dashboard?.current_timer && (
                          <Button
                            onClick={() => startTimer(task.id)}
                            size="sm"
                            className="btn-primary opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`start-timer-${task.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Démarrer
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {tasks.length === 0 && (
                      <div className="text-center py-12 text-[#64748b]">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune tâche assignée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Activity */}
              <Card className="card" data-testid="weekly-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Activité hebdomadaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            background: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value}h`, 'Heures']}
                        />
                        <Bar 
                          dataKey="hours" 
                          fill="#00d4aa" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#64748b]">Total semaine</span>
                      <span className="font-mono font-bold text-[#1a365d]">
                        {weeklyData.reduce((acc, d) => acc + d.hours, 0).toFixed(1)}h
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Hourly Rate Info */}
            <Card className="card mt-6" data-testid="rate-info">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1a365d] rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Votre taux horaire</p>
                      <p className="font-mono text-xl font-bold text-[#1a365d]">
                        {user?.hourly_rate || 0}€/h
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" className="btn-secondary">
                    <Calendar className="w-4 h-4 mr-2" />
                    Générer rapport
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default TeamDashboard;
